import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import {
	Box,
	Text,
	Flex,
	Grid,
	Spinner,
	Button,
} from 'src/components/ui';
import CustomModal from 'src/components/common/CustomModal';
import TemplatePreview from 'src/components/templates/TemplatePreview';
import http from 'src/services/http';
import { toastError, toastSuccess } from 'src/services/toast';
import useCopilotChatContext from 'src/fusion-copilot/hooks/useCopilotChatContext';

interface VideoAdListItem {
	id?: string;
	_id?: string;
	status: string;
	creatomateTemplate?: Record<string, any>;
	catalog?: { id?: string; _id?: string; name: string; image?: string };
	createdAt?: string;
	generatedContent?: { headline?: string; ctaText?: string };
}

const getVideoAdId = (v: VideoAdListItem): string | undefined => v.id || v._id;

interface ImportVideoAdModalProps {
	isOpen: boolean;
	onClose: () => void;
	campaignId: string;
	creativeId: string;
	onImported: () => Promise<void> | void;
}

function normalizeTemplate(template: any): any {
	const clone = JSON.parse(JSON.stringify(template));
	delete clone.output_format;
	if (!clone.duration && clone.elements?.[0]?.duration) {
		clone.duration = clone.elements[0].duration;
	}
	return clone;
}

const ImportVideoAdModal: FC<ImportVideoAdModalProps> = ({
	isOpen,
	onClose,
	campaignId,
	creativeId,
	onImported,
}) => {
	const [loading, setLoading] = useState(false);
	const [videoAds, setVideoAds] = useState<VideoAdListItem[]>([]);
	const [importingId, setImportingId] = useState<string | null>(null);
	const [startingChat, setStartingChat] = useState(false);
	const { openCopilot, createFusionConversation, sendAutoMessage } = useCopilotChatContext();

	const fetchVideoAds = useCallback(async () => {
		setLoading(true);
		try {
			const { data } = await http.get('/v2/video-ads');
			const list: VideoAdListItem[] = Array.isArray(data) ? data : [];
			setVideoAds(list);
		} catch (error: any) {
			toastError(error?.message || 'Failed to load video ads');
		} finally {
			setLoading(false);
		}
	}, []);

	useEffect(() => {
		if (isOpen) {
			fetchVideoAds();
		}
	}, [isOpen, fetchVideoAds]);

	const importableVideoAds = useMemo(
		() => videoAds.filter((v) => v.status === 'completed' && v.creatomateTemplate),
		[videoAds],
	);

	const handleStartCreateVideoAd = useCallback(async () => {
		setStartingChat(true);
		try {
			openCopilot();
			await createFusionConversation();
			// Wait for React to commit the new conversation's state before auto-sending.
			await new Promise((resolve) => setTimeout(resolve, 500));
			if (sendAutoMessage) {
				await sendAutoMessage('Create Video Ad');
			}
			onClose();
		} catch (error: any) {
			toastError(error?.message || 'Failed to open the copilot');
		} finally {
			setStartingChat(false);
		}
	}, [openCopilot, createFusionConversation, sendAutoMessage, onClose]);

	const handleSelect = useCallback(
		async (videoAdId: string) => {
			setImportingId(videoAdId);
			try {
				await http.post(
					`/v2/campaign/${campaignId}/creative/${creativeId}/import-video-ad`,
					{ videoAdId },
				);
				toastSuccess('Video imported into this creative');
				await onImported();
				onClose();
			} catch (error: any) {
				toastError(error?.message || 'Failed to import video');
			} finally {
				setImportingId(null);
			}
		},
		[campaignId, creativeId, onImported, onClose],
	);

	return (
		<CustomModal
			isOpen={isOpen}
			onClose={onClose}
			header={<Text fontSize="xl" fontWeight="bold">Import a generated video</Text>}
			size="4xl"
			maxHeight="80vh"
		>
			<Box p={4}>
				{loading ? (
					<Flex justify="center" align="center" py={12}>
						<Spinner size="lg" />
					</Flex>
				) : importableVideoAds.length === 0 ? (
					<Flex direction="column" align="center" py={12} gap={3}>
						<Text fontSize="lg" fontWeight="medium">No generated videos yet</Text>
						<Text color="gray.500" textAlign="center" maxW="md">
							You haven't generated any video ads yet. Start the flow in the copilot chat and once a video finishes, it will show up here.
						</Text>
						<Button
							mt={2}
							variant="orangeSolid"
							isLoading={startingChat}
							onClick={handleStartCreateVideoAd}
						>
							Create Video Ad
						</Button>
					</Flex>
				) : (
					<Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' }} gap={4}>
						{importableVideoAds.map((videoAd) => {
							const videoAdId = getVideoAdId(videoAd);
							if (!videoAdId) return null;
							const template = normalizeTemplate(videoAd.creatomateTemplate);
							const catalogName = videoAd.catalog?.name || 'Video ad';
							const headline = videoAd.generatedContent?.headline;
							const isImporting = importingId === videoAdId;
							const createdAt = videoAd.createdAt
								? new Date(videoAd.createdAt).toLocaleDateString()
								: '';
							return (
								<Box
									key={videoAdId}
									border="1px solid"
									borderColor="gray.200"
									borderRadius="lg"
									overflow="hidden"
									_hover={{ borderColor: 'orange.400', boxShadow: 'md' }}
									transition="all 0.15s"
								>
									<Box h="200px" bg="gray.50">
										<TemplatePreview
											template={template}
											borderRadius="0"
										/>
									</Box>
									<Box p={3}>
										<Text fontWeight="bold" noOfLines={1} title={catalogName}>
											{catalogName}
										</Text>
										{headline && (
											<Text fontSize="sm" color="gray.600" noOfLines={1}>
												{headline}
											</Text>
										)}
										{createdAt && (
											<Text fontSize="xs" color="gray.400" mt={1}>
												{createdAt}
											</Text>
										)}
										<Button
											mt={3}
											w="full"
											variant="orangeSolid"
											isLoading={isImporting}
											onClick={() => handleSelect(videoAdId)}
										>
											Use this video
										</Button>
									</Box>
								</Box>
							);
						})}
					</Grid>
				)}
			</Box>
		</CustomModal>
	);
};

export default ImportVideoAdModal;
